import React, { useState } from 'react';
import { Form, Input, Button, Card, Typography, Alert, Row, Col, Result } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authService } from '../services/authService';

const { Title, Text } = Typography;

const ResetPassword: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const handleSubmit = async (values: { newPassword: string }) => {
    setLoading(true);
    setError(null);
    try {
      await authService.resetPassword(token, values.newPassword);
      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Password reset failed');
    } finally {
      setLoading(false);
    }
  };

  // Missing token → invalid link.
  if (!token) {
    return (
      <div style={{ minHeight: '100vh', background: '#f0f2f5', padding: '40px 0' }}>
        <Row justify="center" align="middle" style={{ minHeight: '60vh' }}>
          <Col xs={22} sm={16} md={12} lg={8} xl={7}>
            <Card>
              <Result
                status="warning"
                title="Invalid reset link"
                subTitle="This link is missing its token. Please request a new password-reset email."
                extra={[
                  <Button type="primary" key="forgot" onClick={() => navigate('/forgot-password')}>
                    Request New Link
                  </Button>,
                ]}
              />
            </Card>
          </Col>
        </Row>
      </div>
    );
  }

  if (done) {
    return (
      <div style={{ minHeight: '100vh', background: '#f0f2f5', padding: '40px 0' }}>
        <Row justify="center" align="middle" style={{ minHeight: '60vh' }}>
          <Col xs={22} sm={16} md={12} lg={8} xl={7}>
            <Card>
              <Result
                status="success"
                title="Password reset"
                subTitle="Your password has been changed. You can now sign in with your new password."
                extra={[
                  <Button type="primary" key="login" onClick={() => navigate('/login')}>
                    Sign In
                  </Button>,
                ]}
              />
            </Card>
          </Col>
        </Row>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f0f2f5', padding: '40px 0' }}>
      <Row justify="center" align="middle" style={{ minHeight: '60vh' }}>
        <Col xs={22} sm={16} md={12} lg={8} xl={7}>
          <Card>
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <Title level={2}>Reset Password</Title>
              <Text type="secondary">Choose a new password for your account</Text>
            </div>

            {error && (
              <Alert message={error} type="error" showIcon closable onClose={() => setError(null)}
                style={{ marginBottom: 16 }} />
            )}

            <Form form={form} name="resetPassword" onFinish={handleSubmit} layout="vertical" size="large">
              <Form.Item
                name="newPassword"
                label="New Password"
                rules={[
                  { required: true, message: 'Please enter a new password' },
                  { min: 6, message: 'Password must be at least 6 characters' },
                  { max: 20, message: 'Password must be 20 characters or less' },
                ]}
                tooltip="6–20 characters."
              >
                <Input.Password prefix={<LockOutlined />} placeholder="New password" autoComplete="new-password" />
              </Form.Item>

              <Form.Item
                name="confirmPassword"
                label="Confirm New Password"
                dependencies={['newPassword']}
                rules={[
                  { required: true, message: 'Please confirm your password' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('newPassword') === value) return Promise.resolve();
                      return Promise.reject(new Error('Passwords do not match'));
                    },
                  }),
                ]}
              >
                <Input.Password prefix={<LockOutlined />} placeholder="Re-enter new password" autoComplete="new-password" />
              </Form.Item>

              <Form.Item style={{ marginBottom: 0 }}>
                <Button type="primary" htmlType="submit" loading={loading} block size="large">
                  Reset Password
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ResetPassword;
